import { InjectionToken } from '@angular/core';
import { LoginData } from './interfaces/login-data.interface';

export interface SchoolSearchResult {
  id: string;
  name: string;
  slug: string;
}

export interface StudentLoginData {
  username: string;
  password: string;
  orgId: string;
}

export interface CapicheAuthConfig {
  envName: string;
  googleSignInHandler?: () => Promise<void>;
  /** Route to redirect to after login/register. Defaults to '/subscribe'. */
  postAuthRoute?: string;
  /**
   * URL of the Capiche logo image. When set, the auth shell renders the logo
   * centred above the auth card — use this in apps that have no persistent
   * branded header (e.g. teacher-portal). Leave unset when the app already
   * shows the logo in its own header (e.g. web-app).
   */
  logoUrl?: string;
  /**
   * Optional custom registration handler. When provided, replaces the default
   * createUserWithEmailAndPassword flow. Use this when the backend must create
   * the user (e.g. teacher accounts that need role:'teacher' stamped before signup).
   */
  registerHandler?: (data: LoginData) => Promise<void>;
  /**
   * CSW-1379 follow-up: enables the dynamic student-login path on the shared
   * login form. When the identifier field contains no '@', the form reveals an
   * inline "which school?" picker that calls this to search-as-you-type.
   * Omit to disable the student path entirely (e.g. on the register page).
   */
  schoolSearchHandler?: (query: string) => Promise<SchoolSearchResult[]>;
  /**
   * Performs the actual student sign-in (username + password + orgId resolved
   * via schoolSearchHandler). Must call signInWithCustomToken itself, mirroring
   * registerHandler's contract. Required alongside schoolSearchHandler.
   */
  studentLoginHandler?: (data: StudentLoginData) => Promise<void>;
  /**
   * CSW-1381: optional custom forgot-password handler. When provided, replaces
   * the default Firebase sendPasswordResetEmail() call so the app can send a
   * branded Resend email via the backend instead of the unbranded Firebase default.
   *
   * Example (teacher portal app.config.ts):
   *   forgotPasswordHandler: async (email) =>
   *     firstValueFrom(http.post(`${env.baseEducationApiUrl}/auth/forgot-password`, { email }))
   *
   * When omitted, falls back to Firebase sendPasswordResetEmail() (web-app behaviour).
   */
  forgotPasswordHandler?: (email: string) => Promise<void>;
  /**
   * CSW-1549: optional referral-resolution hook, called by the DEFAULT
   * (non-custom-registerHandler) registration path only, right after
   * createUserWithEmailAndPassword succeeds, when the register form's ?ref=
   * code is present. Additive — does NOT replace the default registration
   * flow the way registerHandler does, since apps using this hook (personal/
   * consumer signup) still create the account client-side as before; this
   * just credits the referrer afterward. Errors are swallowed by AuthService
   * (a referral failure must never surface as a registration failure).
   *
   * Example (web-app app.config.ts):
   *   resolveReferralHandler: async (refCode, uid) =>
   *     firstValueFrom(http.post(`${env.baseEducationApiUrl}/referral/resolve-signup`, { refCode }))
   *
   * Omit when the app already has its own registerHandler that resolves
   * referrals server-side as part of account creation (e.g. teacher-portal).
   */
  resolveReferralHandler?: (refCode: string, uid: string) => Promise<void>;
}

export const CAPICHE_AUTH_CONFIG = new InjectionToken<CapicheAuthConfig>('CapicheAuthConfig');
